/**
 * MySQL dashboard templates (S7, remediated S7-R; grown for S21 Storage/
 * Health starter-pack coverage; further remediated 2026-07-17 per a Codex
 * review of commit ef1a44e8f — see the per-template comments below for what
 * changed and why). SQL adapted from the same catalog views the Session
 * Browser (`electron/session-browser/providers/mysqlSessionQueries.ts`) and
 * Performance Dashboard (`electron/performance-dashboard/providers/mysqlPerformanceDashboardQueries.ts`)
 * already query in production. No `jobs`/`backup` categories: MySQL has no
 * built-in job scheduler with failure history and no server-visible backup
 * status — absence is correct here, not a gap.
 *
 * Only two templates gate on `requiredCapabilities: ['mysql-not-mariadb']`
 * (`templateCapabilities.ts`, S7-R M1) — `sessions.lock_waits` and
 * `indexes.unused_indexes`, both of which genuinely need MySQL 8.0+/5.7.7+
 * surfaces MariaDB lacks (`performance_schema.data_lock_waits`, the bundled
 * `sys` schema) regardless of its own MySQL-shaped, numerically-higher
 * version string. The four newer templates (`performance.connection_saturation`,
 * `storage.largest_tables`, `storage.reclaimable_space`,
 * `indexes.table_scan_ratio`) deliberately stay ungated, but on different
 * (corrected) grounds than before:
 *  - `connection_saturation` reads only `INFORMATION_SCHEMA.PROCESSLIST` and
 *    the `@@GLOBAL.max_connections` system variable — both universal across
 *    MySQL and MariaDB, with zero `performance_schema` dependency. The prior
 *    version instead read `performance_schema.global_status`/
 *    `global_variables`, which the Codex review caught as NOT
 *    MariaDB-compatible as claimed: MariaDB keeps `GLOBAL_VARIABLES` in
 *    `INFORMATION_SCHEMA`, not `performance_schema`, and
 *    `performance_schema.global_status` only exists from MariaDB 10.5.2 —
 *    that version was silently broken on MariaDB and on MySQL <5.7.6.
 *  - `storage.largest_tables` and `storage.reclaimable_space` read
 *    `INFORMATION_SCHEMA.TABLES` scoped to `TABLE_SCHEMA = DATABASE()` — the
 *    tile's selected database only (matching `database_sizes`'s per-schema
 *    framing), not every schema on the server as before.
 *  - `indexes.table_scan_ratio` reads the long-standing
 *    `performance_schema.table_io_waits_summary_by_index_usage` table,
 *    present on both engines — though Performance Schema instrumentation
 *    ships OFF by default on MariaDB (see its `emptyStateCopy`).
 * All four still favor broad runnability over an unnecessary capability
 * gate. All templates were live-verified against a real MySQL 8.0.45 server
 * at 127.0.0.1:3306 (`sakila` database).
 */
import type { DashboardTemplate } from '../templateTypes';

export const mysqlTemplates: DashboardTemplate[] = [
  {
    id: 'mysql.sessions.active_queries',
    engine: 'mysql',
    title: 'Active queries',
    description: 'Non-sleeping connections ordered by run time — the first thing to check when the database feels slow. Requires PROCESS privilege to see other sessions (own connection is always visible).',
    category: 'sessions',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No active queries right now.',
    sql: `SELECT
  ID AS process_id,
  USER AS db_user,
  HOST AS host,
  DB AS db_name,
  COMMAND AS command,
  TIME AS running_seconds,
  LEFT(INFO, 500) AS query_text
FROM INFORMATION_SCHEMA.PROCESSLIST
WHERE ID != CONNECTION_ID()
  AND COMMAND != 'Sleep'
  AND {{dashboard_filters}}
ORDER BY TIME DESC`,
  },
  {
    id: 'mysql.sessions.lock_waits',
    engine: 'mysql',
    title: 'InnoDB lock waits',
    description: 'Transactions currently blocked on a row lock, shown with their blocker — catch a stuck transaction before it queues up the rest. Requires performance_schema (on by default) and MySQL 8.0+ (not MariaDB, which has no data_lock_waits).',
    category: 'sessions',
    fits: ['table'],
    defaultTileType: 'table',
    minimumVersion: '8.0.1',
    requiredCapabilities: ['mysql-not-mariadb'],
    expectedShape: 'rows',
    emptyStateCopy: 'No transactions are currently waiting on a lock.',
    sql: `SELECT
  w.REQUESTING_ENGINE_TRANSACTION_ID AS waiting_trx_id,
  wt.PROCESSLIST_ID AS waiting_processlist_id,
  wt.PROCESSLIST_USER AS waiting_user,
  w.BLOCKING_ENGINE_TRANSACTION_ID AS blocking_trx_id,
  bt.PROCESSLIST_ID AS blocking_processlist_id,
  bt.PROCESSLIST_USER AS blocking_user,
  dl.OBJECT_SCHEMA AS object_schema,
  dl.OBJECT_NAME AS object_name,
  dl.LOCK_TYPE AS lock_type,
  dl.LOCK_MODE AS lock_mode
FROM performance_schema.data_lock_waits w
JOIN performance_schema.threads wt ON w.REQUESTING_THREAD_ID = wt.THREAD_ID
JOIN performance_schema.threads bt ON w.BLOCKING_THREAD_ID = bt.THREAD_ID
LEFT JOIN performance_schema.data_locks dl
  ON dl.ENGINE_TRANSACTION_ID = w.REQUESTING_ENGINE_TRANSACTION_ID AND dl.LOCK_STATUS = 'WAITING'
WHERE {{dashboard_filters}}
ORDER BY wt.PROCESSLIST_ID`,
  },
  {
    id: 'mysql.sessions.long_running_transactions',
    engine: 'mysql',
    title: 'Long-running transactions',
    description: 'Open InnoDB transactions ordered by start time, oldest first — a long-lived transaction holds row locks and blocks purge. Works on MySQL 5.7+ and MariaDB.',
    category: 'sessions',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No open InnoDB transactions right now.',
    sql: `SELECT
  trx_id,
  trx_state,
  trx_started,
  TIMESTAMPDIFF(SECOND, trx_started, NOW()) AS running_seconds,
  trx_mysql_thread_id AS processlist_id,
  LEFT(trx_query, 500) AS query_text
FROM information_schema.INNODB_TRX
WHERE {{dashboard_filters}}
ORDER BY trx_started ASC`,
  },
  {
    id: 'mysql.performance.top_waits',
    engine: 'mysql',
    title: 'Top wait events',
    description: 'Aggregate wait statistics since the last reset — the dominant wait event tells you what the engine is starving on.',
    category: 'performance',
    fits: ['chart', 'table'],
    defaultTileType: 'chart',
    expectedShape: 'rows',
    emptyStateCopy: 'No accumulated wait time yet.',
    sql: `SELECT
  EVENT_NAME AS event_name,
  COUNT_STAR AS count_star,
  SUM_TIMER_WAIT / 1000000000 AS total_wait_ms
FROM performance_schema.events_waits_summary_global_by_event_name
WHERE COUNT_STAR > 0
  AND EVENT_NAME != 'idle'
  AND {{dashboard_filters}}
ORDER BY SUM_TIMER_WAIT DESC
LIMIT 30`,
  },
  {
    id: 'mysql.performance.buffer_pool_hit_ratio',
    engine: 'mysql',
    title: 'InnoDB buffer pool hit ratio',
    description: 'Share of page reads served from the buffer pool instead of disk — a sustained drop means the pool is undersized for the working set.',
    category: 'performance',
    fits: ['stat'],
    defaultTileType: 'stat',
    statValueColumn: 'buffer_pool_hit_ratio_pct',
    expectedShape: 'scalar',
    // Cross join of two always-one-row global_status lookups — this always
    // returns exactly one row (M23): "no data" can't happen, only a
    // low/high ratio can.
    emptyStateCopy: 'Always returns one row — a low ratio means the buffer pool is undersized for your working set; healthy servers usually sit above 95%.',
    sql: `SELECT
  ROUND(100.0 * (1 - (
    (SELECT VARIABLE_VALUE FROM performance_schema.global_status WHERE VARIABLE_NAME = 'Innodb_buffer_pool_reads')
    / NULLIF((SELECT VARIABLE_VALUE FROM performance_schema.global_status WHERE VARIABLE_NAME = 'Innodb_buffer_pool_read_requests'), 0)
  )), 2) AS buffer_pool_hit_ratio_pct
FROM DUAL
WHERE {{dashboard_filters}}`,
  },
  {
    id: 'mysql.performance.connection_saturation',
    engine: 'mysql',
    title: 'Connection saturation',
    description: 'Open connections (INFORMATION_SCHEMA.PROCESSLIST) as a share of @@GLOBAL.max_connections — approaching 100% means new connections start getting refused; full visibility needs the PROCESS privilege, without which only your own threads are counted.',
    category: 'performance',
    fits: ['stat'],
    defaultTileType: 'stat',
    statValueColumn: 'connections_used_pct',
    expectedShape: 'scalar',
    // Always returns exactly one row: COUNT(*) over PROCESSLIST is never
    // empty (the connection running this query is itself a row) and
    // @@GLOBAL.max_connections is always defined — "no data" can't happen,
    // only a low/high ratio can.
    // Deliberately NOT gated on `mysql-not-mariadb`: INFORMATION_SCHEMA.PROCESSLIST
    // and `@@GLOBAL.<var>` system-variable syntax are both universal across
    // MySQL and MariaDB, with zero performance_schema dependency (see the
    // file header for why the prior performance_schema-based version was
    // wrong). Live-verified against MySQL 8.0.45 below; MariaDB
    // compatibility is reasoned, not tested (no MariaDB server available in
    // this environment).
    emptyStateCopy: 'Always returns one row — the share of max_connections currently used by open connections (or just your own connection if the PROCESS privilege is missing); healthy servers usually stay well under 80%.',
    sql: `SELECT
  CAST(COUNT(*) AS UNSIGNED) AS client_connections,
  CAST(@@GLOBAL.max_connections AS UNSIGNED) AS max_connections,
  ROUND(100.0 * CAST(COUNT(*) AS DECIMAL(18,4))
    / NULLIF(CAST(@@GLOBAL.max_connections AS DECIMAL(18,4)), 0), 1) AS connections_used_pct
FROM INFORMATION_SCHEMA.PROCESSLIST
WHERE {{dashboard_filters}}`,
  },
  {
    id: 'mysql.storage.database_sizes',
    engine: 'mysql',
    title: 'Database sizes',
    description: 'Data + index size per schema — spot the one growing faster than disk was budgeted for.',
    category: 'storage',
    fits: ['chart', 'table'],
    defaultTileType: 'chart',
    expectedShape: 'rows',
    emptyStateCopy: 'No user schemas found on this server.',
    sql: `SELECT
  TABLE_SCHEMA AS database_name,
  ROUND(SUM(DATA_LENGTH + INDEX_LENGTH) / 1024 / 1024, 1) AS size_mb
FROM INFORMATION_SCHEMA.TABLES
WHERE TABLE_SCHEMA NOT IN ('mysql', 'information_schema', 'performance_schema', 'sys')
  AND {{dashboard_filters}}
GROUP BY TABLE_SCHEMA
ORDER BY size_mb DESC`,
  },
  {
    id: 'mysql.storage.tmp_tables_to_disk',
    engine: 'mysql',
    title: 'Temp tables spilling to disk',
    description: 'Share of temporary tables MySQL had to materialize on disk instead of in memory — a rising trend means queries or tmp_table_size need attention.',
    category: 'storage',
    fits: ['stat'],
    defaultTileType: 'stat',
    statValueColumn: 'tmp_tables_to_disk_pct',
    expectedShape: 'scalar',
    // Cross join of two always-one-row global_status lookups — this always
    // returns exactly one row (M23), even before any temp table exists
    // (0/0 -> NULL percentage, not zero rows).
    emptyStateCopy: 'Always returns one row — 0% (or blank, before any temp table has been created) means everything has stayed in memory; a rising percentage means tmp_table_size needs attention.',
    sql: `SELECT
  ROUND(100.0 * disk_tmp.VARIABLE_VALUE / NULLIF(total_tmp.VARIABLE_VALUE, 0), 2) AS tmp_tables_to_disk_pct
FROM
  (SELECT VARIABLE_VALUE FROM performance_schema.global_status WHERE VARIABLE_NAME = 'Created_tmp_disk_tables') disk_tmp,
  (SELECT VARIABLE_VALUE FROM performance_schema.global_status WHERE VARIABLE_NAME = 'Created_tmp_tables') total_tmp
WHERE {{dashboard_filters}}`,
  },
  {
    id: 'mysql.storage.largest_tables',
    engine: 'mysql',
    title: 'Largest tables in the current database',
    description: 'The biggest tables by data + index size in the currently selected database, matching the tile\'s database selection — the tables most likely to dominate disk usage and backup time.',
    category: 'storage',
    fits: ['table', 'chart'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    // Scoped to `TABLE_SCHEMA = DATABASE()` (Codex review of ef1a44e8f,
    // finding M4): the prior version scanned INFORMATION_SCHEMA.TABLES
    // across every schema on the server on every refresh, regardless of
    // which database the tile was pointed at.
    emptyStateCopy: 'No user tables found in the current database — this is also what you see if no default database is selected (DATABASE() is NULL, which matches zero rows here).',
    sql: `SELECT
  TABLE_SCHEMA AS table_schema,
  TABLE_NAME AS table_name,
  ROUND(DATA_LENGTH / 1024 / 1024, 1) AS data_mb,
  ROUND(INDEX_LENGTH / 1024 / 1024, 1) AS index_mb,
  ROUND((DATA_LENGTH + INDEX_LENGTH) / 1024 / 1024, 1) AS total_mb,
  TABLE_ROWS AS approx_rows
FROM INFORMATION_SCHEMA.TABLES
WHERE TABLE_SCHEMA = DATABASE()
  AND TABLE_TYPE = 'BASE TABLE'
  AND {{dashboard_filters}}
ORDER BY (DATA_LENGTH + INDEX_LENGTH) DESC
LIMIT 20`,
  },
  {
    id: 'mysql.storage.reclaimable_space',
    engine: 'mysql',
    title: 'Allocated unused space (estimate)',
    description: 'InnoDB tables in the current database with free space allocated inside their tablespace file — DATA_FREE is a per-TABLESPACE figure (it repeats across every table sharing one, and OPTIMIZE TABLE will not shrink a shared tablespace), so treat this as a rough estimate rather than a per-table reclaimable amount; only file-per-table tablespaces reclaim independently.',
    category: 'storage',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    // Filtered to ENGINE = 'InnoDB' and scoped to `TABLE_SCHEMA = DATABASE()`
    // (Codex review of ef1a44e8f, findings M2 + M4): DATA_FREE is only
    // meaningful for InnoDB, and the prior version scanned every schema on
    // the server instead of the tile's selected database.
    emptyStateCopy: 'No InnoDB tables in the current database currently show allocated unused space — this is also what you see if no default database is selected (DATABASE() is NULL, which matches zero rows here).',
    sql: `SELECT
  TABLE_SCHEMA AS table_schema,
  TABLE_NAME AS table_name,
  ENGINE AS storage_engine,
  ROUND(DATA_FREE / 1024 / 1024, 1) AS unused_mb,
  ROUND((DATA_LENGTH + INDEX_LENGTH) / 1024 / 1024, 1) AS total_mb
FROM INFORMATION_SCHEMA.TABLES
WHERE TABLE_SCHEMA = DATABASE()
  AND TABLE_TYPE = 'BASE TABLE'
  AND ENGINE = 'InnoDB'
  AND DATA_FREE > 0
  AND {{dashboard_filters}}
ORDER BY DATA_FREE DESC
LIMIT 20`,
  },
  {
    id: 'mysql.indexes.table_scan_ratio',
    engine: 'mysql',
    title: 'Rows read without an index',
    description: 'Tables with the highest share of row I/O served without an index, heaviest first, accumulated since the server started (or since Performance Schema stats were last reset) — small tables are routinely read in full, so a high percentage there is normal; the signal to watch for is a large table high on this list.',
    category: 'indexes',
    fits: ['table', 'chart'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    // Deliberately NOT gated on `mysql-not-mariadb`: table_io_waits_summary_by_index_usage
    // has been part of performance_schema since it was introduced in 5.6 —
    // unlike data_lock_waits (MySQL 8.0-only), MariaDB carries this table
    // too. Live-verified against MySQL 8.0.45 below; MariaDB compatibility
    // is reasoned, not tested (no MariaDB server available here).
    // Retitled/reworded (Codex review of ef1a44e8f, finding M3): COUNT_READ
    // counts row I/O operations, not scan statements, so the old "full table
    // scans" framing overstated what this measures.
    emptyStateCopy: 'No read activity recorded — either nothing has read these tables yet, or table I/O instrumentation is disabled (Performance Schema ships OFF by default on MariaDB; confirm performance_schema and the table_io_waits_summary_by_index_usage consumer/instrument are both enabled).',
    sql: `SELECT
  OBJECT_SCHEMA AS table_schema,
  OBJECT_NAME AS table_name,
  SUM(CASE WHEN INDEX_NAME IS NULL THEN COUNT_READ ELSE 0 END) AS reads_without_index,
  SUM(CASE WHEN INDEX_NAME IS NOT NULL THEN COUNT_READ ELSE 0 END) AS reads_with_index,
  ROUND(100.0 * SUM(CASE WHEN INDEX_NAME IS NULL THEN COUNT_READ ELSE 0 END)
    / NULLIF(SUM(COUNT_READ), 0), 1) AS pct_without_index
FROM performance_schema.table_io_waits_summary_by_index_usage
WHERE OBJECT_SCHEMA NOT IN ('mysql', 'performance_schema', 'information_schema', 'sys')
  AND COUNT_READ > 0
  AND {{dashboard_filters}}
GROUP BY OBJECT_SCHEMA, OBJECT_NAME
HAVING SUM(COUNT_READ) > 0
ORDER BY reads_without_index DESC
LIMIT 30`,
  },
  {
    id: 'mysql.indexes.unused_indexes',
    engine: 'mysql',
    title: 'Unused indexes',
    description: 'Indexes the sys schema has never seen scanned since Performance Schema statistics began — dead weight on every write, worth dropping after you confirm. Requires the sys schema (bundled since 5.7.7, not on MariaDB) + PROCESS/SELECT privilege.',
    category: 'indexes',
    fits: ['table'],
    defaultTileType: 'table',
    minimumVersion: '5.7.7',
    requiredCapabilities: ['mysql-not-mariadb'],
    expectedShape: 'rows',
    emptyStateCopy: 'Every index has been scanned at least once since statistics began.',
    sql: `SELECT
  object_schema,
  object_name,
  index_name
FROM sys.schema_unused_indexes
WHERE {{dashboard_filters}}
ORDER BY object_schema, object_name`,
  },
];
